import { useState } from "react";
import { appel, DECISIONS, heure, MOMENTS, MOTIFS, quand, type Signalement } from "./api.js";
import { agir, Info, Point, Retour, useLecture } from "./commun.js";
import { useRegie } from "./Regie.js";

/**
 * Les signalements (A9, A10) : la même file pour la ville et pour la régie
 * d'un compte — chacune ne voit que les siens. Pas de pseudo : des lettres,
 * un motif, un moment, et le seul message visé, effacé après la décision.
 */

const DELAI_MS = 48 * 3_600_000;

const statut = (s: Signalement): JSX.Element | string => {
  if (s.statut === "nouveau") return <span className="urg">Nouveau</span>;
  if (s.statut === "en_cours") return "En cours";
  return s.decision ? (DECISIONS[s.decision.type] ?? "Traité") : "Traité";
};

const reste = (s: Signalement, maintenant: number): string => {
  if (s.statut === "traite") return "—";
  const h = Math.ceil((s.recuA + DELAI_MS - maintenant) / 3_600_000);
  return h > 0 ? `${h} h` : "dépassé";
};

export function ListeSignalements(props: { espace: "ville" | "regie" }): JSX.Element {
  const { aller } = useRegie();
  const [filtre, setFiltre] = useState<"nouveau" | "en_cours" | "traite" | "tous">("nouveau");
  const l = useLecture(() => appel<{ a: number; signalements: Signalement[] }>(`/api/regie/signalements?espace=${props.espace}`), 15_000, [props.espace]);
  const tous = l.donnees?.signalements ?? [];
  const maintenant = l.donnees?.a ?? Date.now();
  const compte = (s: string): number => tous.filter((x) => x.statut === s).length;
  const visibles = filtre === "tous" ? tous : tous.filter((x) => x.statut === filtre);
  const chips = [
    ["nouveau", `Nouveaux · ${compte("nouveau")}`],
    ["en_cours", `En cours · ${compte("en_cours")}`],
    ["traite", `Traités · ${compte("traite")}`],
    ["tous", "Tous"],
  ] as const;
  return (
    <>
      <div className="hd">
        <div>
          <h1>
            Signalements
            <Info texte="Délai cible : 48 h. Chaque décision est expliquée à la personne signalée." />
          </h1>
        </div>
      </div>
      <div className="chips">
        {chips.map(([k, lib]) => (
          <button type="button" key={k} className={filtre === k ? "chip on" : "chip"} aria-pressed={filtre === k} onClick={() => setFiltre(k)}>
            {lib}
          </button>
        ))}
      </div>
      {l.erreur ? <p className="erreur">{l.erreur}</p> : null}
      {l.donnees && visibles.length === 0 ? <div className="vide">Rien ici.</div> : null}
      {visibles.length > 0 ? (
        <table>
          <thead>
            <tr>
              <th>Reçu</th>
              <th>Motif</th>
              <th>{props.espace === "ville" ? "Tribu" : "Table"}</th>
              <th>Moment</th>
              <th>Reste</th>
              <th>Statut</th>
            </tr>
          </thead>
          <tbody>
            {visibles.map((s) => (
              <tr key={s.id} className="cliquable" onClick={() => aller({ espace: props.espace, page: "signalements", id: s.id } as never)}>
                <td>{quand(s.recuA, maintenant)}</td>
                <td>
                  <a
                    href="#"
                    onClick={(e) => {
                      e.preventDefault();
                    }}
                  >
                    {MOTIFS[s.motif] ?? s.motif}
                  </a>
                </td>
                <td>
                  <span className="nom">
                    <Point couleur={s.tribu.couleur} />
                    {s.tribu.nom} · {s.tribu.lieu}
                  </span>
                </td>
                <td>{MOMENTS[s.phase] ?? s.phase}</td>
                <td>{reste(s, maintenant)}</td>
                <td>{statut(s)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : null}
    </>
  );
}

const CHOIX_PUBLIC: Array<{ type: string; titre: string; detail: string }> = [
  { type: "classer", titre: "Classer sans suite", detail: "Rien à retirer." },
  { type: "retirer", titre: "Contenu retiré", detail: "Le contenu a été retiré ou la tribu fermée." },
];

const CHOIX_PHOTO: Array<{ type: string; titre: string; detail: string }> = [
  { type: "classer", titre: "Classer sans suite", detail: "La photo reste." },
  { type: "retirer", titre: "Retirer la photo", detail: "Elle disparaît du compte et de la tribu." },
  { type: "avertir", titre: "Retirer et avertir", detail: "Hoa lui écrit pourquoi, au prochain lancement." },
  { type: "suspendre", titre: "Retirer et suspendre 7 jours", detail: "Plus de tribu pendant 7 jours, motif affiché." },
];

const CHOIX: Array<{ type: string; titre: string; detail: string }> = [
  { type: "classer", titre: "Classer sans suite", detail: "Rien ne se passe pour la personne signalée." },
  { type: "avertir", titre: "Avertir", detail: "Hoa lui écrit pourquoi, au prochain lancement." },
  { type: "suspendre", titre: "Suspendre 7 jours", detail: "Plus de tribu pendant 7 jours, motif affiché." },
  { type: "bannir", titre: "Bannir", detail: "Compte fermé. Sans compte : cet appareil." },
];

export function DetailSignalement(props: { espace: "ville" | "regie"; id: string }): JSX.Element {
  const { aller, relireCompteurs } = useRegie();
  const l = useLecture(() => appel<{ signalement: Signalement }>(`/api/regie/signalements/${props.id}`), null, [props.id]);
  const [choix, setChoix] = useState("classer");
  const [motif, setMotif] = useState("");
  const s = l.donnees?.signalement ?? null;
  // L'ancien formulaire public (contenu illicite, problème après une soirée) ne visait personne.
  const public_ = s ? (s.motif === "illicite" || s.motif === "apres") && !s.cible : false;
  const choix_ = public_ ? CHOIX_PUBLIC : s?.motif === "photo" ? CHOIX_PHOTO : CHOIX;
  const retour = (): void => aller({ espace: props.espace, page: "signalements" } as never);
  const maintenant = Date.now();

  return (
    <>
      <div className="hd">
        <button type="button" className="rond" aria-label="Retour aux signalements" onClick={retour}>
          <Retour />
        </button>
        <div>
          <h1 style={{ fontSize: 30 }}>{s ? (MOTIFS[s.motif] ?? s.motif) : "Signalement"}</h1>
          {s ? (
            <div className="sub">
              Reçu {quand(s.recuA, maintenant).toLowerCase()}
              {s.statut !== "traite" ? ` · reste ${reste(s, maintenant)}` : ""}
            </div>
          ) : null}
        </div>
        <span className="sp" />
        {s ? statut(s) : null}
      </div>
      {l.erreur ? <p className="erreur">{l.erreur}</p> : null}
      {s ? (
        <div style={{ display: "grid", gridTemplateColumns: "minmax(0, 1.25fr) minmax(0, 1fr)", gap: 22, alignItems: "start" }}>
          <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
            <section className="carte" style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              <span className="etiq">Où</span>
              <span className="nom">
                <Point couleur={s.tribu.couleur} />
                <b>
                  {s.tribu.nom} · {s.tribu.lieu}
                </b>
              </span>
              <span className="sub">
                {s.tribu.sujet ? `${s.tribu.sujet} · ` : ""}
                {MOMENTS[s.phase] ?? s.phase}
              </span>
            </section>
            <section className="carte" style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              <span className="etiq">
                {public_ || s.phase === "après la soirée" ? "Ce qui est signalé" : "Message signalé"}
                <Info texte="Seul contenu conservé, effacé après la décision." />
              </span>
              {s.photo ? (
                <img src={s.photo} alt="Photo de profil signalée" style={{ width: 160, height: 160, borderRadius: 16, objectFit: "cover" }} />
              ) : null}
              {s.message ? (
                <blockquote style={{ margin: 0, padding: "12px 16px", borderRadius: "6px 16px 16px 16px", background: "#FFFFFF", color: "#111111", font: "700 17px/1.35 'Atkinson Hyperlegible', sans-serif" }}>
                  {s.message}
                </blockquote>
              ) : (
                s.photo ? null : (
                  <span className="sub">{s.statut === "traite" ? "Effacé après la décision." : "Aucun message visé."}</span>
                )
              )}
            </section>
            {public_ ? (
              <section className="carte" style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                <span className="etiq">Personnes</span>
                <span>Formulaire public : personne n'est visé, l'auteur reste anonyme.</span>
              </section>
            ) : (
              <section className="carte" style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                <span className="etiq">Personnes</span>
                <span>
                  <b>Signalée :</b> {s.cible ? `membre ${s.cible} de la ${props.espace === "ville" ? "tribu" : "table"}` : "non précisée"}
                  {s.aSanction === false ? " · sans compte" : ""}
                </span>
                <span>
                  <b>Qui signale :</b> membre {s.auteur}
                  {s.eviter ? " · a demandé à ne plus jamais être dans sa tribu (appliqué)" : ""}
                </span>
              </section>
            )}
          </div>
          {s.decision ? (
            <section className="carte" style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              <h2 className="h2">Décision</h2>
              <b>
                {DECISIONS[s.decision.type] ?? s.decision.type}
                {s.levee ? " · levée" : ""}
              </b>
              {s.decision.motif ? <span>« {s.decision.motif} »</span> : null}
              <span className="sub">Le {new Date(s.decision.a).toLocaleDateString("fr-FR")} à {heure(s.decision.a)}.</span>
              {s.contestation ? (
                <>
                  <span className="etiq">Contestation, le {new Date(s.contestation.a).toLocaleDateString("fr-FR")}</span>
                  <blockquote style={{ margin: 0, padding: "12px 16px", borderRadius: "6px 16px 16px 16px", background: "#FFFFFF", color: "#111111", fontWeight: 700 }}>
                    {s.contestation.texte}
                  </blockquote>
                </>
              ) : null}
              {(s.decision.type === "suspendre" || s.decision.type === "bannir") && !s.levee ? (
                <button
                  type="button"
                  className="btn q"
                  style={{ alignSelf: "flex-start" }}
                  onClick={() =>
                    void agir(() => appel(`/api/regie/signalements/${s.id}/lever`, { method: "POST" }), "Sanction levée.").then(async () => {
                      await relireCompteurs();
                      await l.relire();
                    })
                  }
                >
                  Lever la sanction
                </button>
              ) : null}
            </section>
          ) : (
            <section className="carte" style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              <h2 className="h2">Décision</h2>
              {choix_.map((c) => {
                const on = choix === c.type;
                return (
                  <label
                    key={c.type}
                    title={c.detail}
                    style={{
                      display: "flex",
                      gap: 12,
                      alignItems: "flex-start",
                      padding: "10px 12px",
                      borderRadius: 14,
                      background: on ? "#FFFFFF" : "#262626",
                      color: on ? "#111111" : "#FFFFFF",
                      cursor: "pointer",
                    }}
                  >
                    <input type="radio" name="decision" checked={on} onChange={() => setChoix(c.type)} style={{ marginTop: 4, width: 18, height: 18 }} />
                    <b>{c.titre}</b>
                  </label>
                );
              })}
              {choix !== "classer" && s.aSanction === false && !public_ ? (
                <span className="sub">Sans compte : la décision n'a pas d'effet sur elle.</span>
              ) : null}
              <div className="champ">
                <label htmlFor="rg-motif">{public_ ? "Note (facultative)" : "Motif, lu par la personne signalée"}</label>
                <input id="rg-motif" className="in" maxLength={200} value={motif} onChange={(e) => setMotif(e.target.value)} />
              </div>
              <button
                type="button"
                className="btn p"
                style={{ alignSelf: "flex-end" }}
                disabled={!public_ && choix !== "classer" && motif.trim().length < 3}
                onClick={() =>
                  void agir(() => appel(`/api/regie/signalements/${s.id}/decision`, { method: "POST", corps: { type: choix, motif } }), "Décision enregistrée.").then(async (ok) => {
                    await relireCompteurs();
                    if (ok) retour();
                  })
                }
              >
                Enregistrer la décision
              </button>
            </section>
          )}
        </div>
      ) : null}
    </>
  );
}
